import { FC, useEffect, useState } from 'react';
import {
  Descriptions,
  Modal,
  ModalProps,
  Select,
  Table,
  TableColumnsType,
  Button as AntButton,
  Radio,
  RadioChangeEvent,
} from 'antd';
import { getDetailCode, getDetailCodeLabel } from '../../lib/util';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store/rootReducer';
import useAxios from '../../hooks/useAxios';
import {
  IAnswerInfo,
  IExamCheckAnswerCard,
  IExamCheckAnswerCardBody,
  IGetExamCheckAnswer,
  IGetExamCheckAnswerCard,
  IGetExamCheckSubjectInfo,
} from '../../models/testCenter.model';
import {
  getExamCheckAnswer,
  getExamCheckAnswerCard,
  getExamCheckSubjectInfo,
  postExamCheckAnswerCard,
} from '../../api/testCenter.api';
import Loader from '../Loader';
import ErrorResponse from '../ErrorResponse';
import useNotification from '../../hooks/useNotification';
import {
  generateAnswerColumns,
  generateAnswerRows,
  readAnswerExcel,
  setAnswerStyle,
} from '../../lib/excel/AnswerModalExcel';
import { generateExcel } from '../../lib/excel';
import useFile from '../../hooks/useFile';

interface IProps extends Pick<ModalProps, 'visible' | 'onCancel'> {
  examCode: number;
  examTestCode: number;
  defaultExamSubjectCode?: number;
  isFullMode?: boolean;
}

const { Option } = Select;

const answer4 = ['A', 'B', 'C', 'D'];

const answer5 = ['A', 'B', 'C', 'D', 'E'];

const AnswerModal: FC<IProps> = ({
  visible,
  onCancel,
  examCode,
  examTestCode,
  defaultExamSubjectCode,
  isFullMode,
}) => {
  const { t } = useTranslation();
  const {
    data: res,
    isLoading,
    error,
    execute,
  } = useAxios<IGetExamCheckAnswer>();
  const {
    data: res2,
    isLoading: isLoading2,
    error: error2,
    execute: execute2,
  } = useAxios<IGetExamCheckSubjectInfo>();
  const {
    data: res3,
    isLoading: isLoading3,
    error: error3,
    execute: execute3,
  } = useAxios<IGetExamCheckAnswerCard>();
  const {
    data: submitRes,
    isLoading: submitIsLoading,
    error: submitError,
    execute: submitExecute,
  } = useAxios<IGetExamCheckAnswerCard>();
  const { file, upload } = useFile();
  const { successNotification, warningNotification, infoNotification } =
    useNotification();
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);

  const [examSubjectCode, setExamSubjectCode] = useState<number | undefined>();
  const [userAnswers, setUserAnswers] = useState<string[]>([]);
  const [answerCards, setAnswerCards] = useState<
    IExamCheckAnswerCard | undefined
  >();
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [fullMode, setFullMode] = useState(true);

  useEffect(() => {
    if (isFullMode === false) {
      setFullMode(isFullMode);
    }
  }, [isFullMode]);

  useEffect(() => {
    if (examCode && examTestCode) {
      execute(getExamCheckAnswer(examCode, examTestCode));
      execute2(getExamCheckSubjectInfo(examCode));  
    }
  }, [examCode, examTestCode]);

  useEffect(() => {
    if (res2) {
      const _examSubjectCode = defaultExamSubjectCode
        ? defaultExamSubjectCode
        : res2.data[0].examSubjectCode
      setExamSubjectCode(_examSubjectCode);
      execute3(
        getExamCheckAnswerCard(examTestCode, _examSubjectCode),
      );
    }
  }, [res2]);

  useEffect(() => {
    if (res2 && examSubjectCode) {
      execute3(getExamCheckAnswerCard(examTestCode, examSubjectCode));
    }
  }, [res2, examSubjectCode]);

  useEffect(() => {
    if (res3?.data) {
      setUserAnswers(
        res3.data.answerInfo.map(({ userAnswer }) => userAnswer?.toUpperCase()),
      );
      setAnswerCards(res3.data);
    }
  }, [res3]);

  useEffect(() => {
    if (submitRes && !submitIsLoading) {
      successNotification(t('common.message.save1'));
      setAnswerCards(submitRes.data);
    }
  }, [submitRes, submitIsLoading]);

  const TE0001 = getDetailCode(commonCodes, 'TE0001');

  const onUserAnswerChange = (
    { target: { value } }: RadioChangeEvent,
    index: number,
  ) => {
    const newUserAnswers = [...userAnswers];
    newUserAnswers[index] = value;
    setUserAnswers(newUserAnswers);
  };

  const showConfirm = () => {
    const userAnswerEmptyFilter = userAnswers.filter(
      (value) => value === undefined || value === null,
    );

    if (userAnswerEmptyFilter.length >= 1)
      return infoNotification(t('cbt.test.message9'));
    setConfirmVisible(true);
  };

  const closeConfirm = () => setConfirmVisible(false);

  const onSubmit = () => {
    if (answerCards && examTestCode && examSubjectCode) {
      const body: IExamCheckAnswerCardBody[] = answerCards.answerInfo.map(
        ({ quizCode }, index) => {
          return { quizCode, userAnswer: userAnswers[index] };
        },
      );
      submitExecute(
        postExamCheckAnswerCard(examTestCode, examSubjectCode, body),
      );
      closeConfirm();
    }
  };

  const columns: TableColumnsType<IAnswerInfo> = [
    {
      title: t('cbt.test.column18'),
      dataIndex: 'quizSeq',
      align: 'center',
    },
    {
      title: t('cbt.test.column31'),
      dataIndex: 'userAnswer',
      align: 'center',
      render: (value: string, { quizType }, index) => (
        <div className='auth-radio__area'>
          <Radio.Group
            onChange={(e) => onUserAnswerChange(e, index)}
            value={userAnswers[index]}
          >
            {quizType === 41
              ? answer4.map((item, index) => (
                  <Radio key={`answer-${index}`} value={item}>
                    {item.toUpperCase()}
                  </Radio>
                ))
              : answer5.map((item, index) => (
                  <Radio key={`answer-${index}`} value={item}>
                    {item.toUpperCase()}
                  </Radio>
                ))}
          </Radio.Group>
        </div>
      ),
    },
    {
      title: t('cbt.test.column19'),
      dataIndex: 'quizAnswer',
      align: 'center',
      render: (value: string) => value?.toUpperCase(),
    },
    {
      title: t('cbt.test.column26'),
      dataIndex: 'score',
      align: 'center',
    },
  ];

  const summaryColumns: TableColumnsType<IAnswerInfo> = [
    {
      title: t('cbt.test.column18'),
      dataIndex: 'quizSeq',
      align: 'center',
    },
    {
      title: t('cbt.test.column19'),
      dataIndex: 'quizAnswer',
      align: 'center',
      render: (value: string) => value?.toUpperCase(),
    },
  ];

  const summary = () => {
    return (
      <Table.Summary.Row>
        <Table.Summary.Cell index={0} colSpan={3} className="text-center">
          {t('cbt.test.column22')}
        </Table.Summary.Cell>
        <Table.Summary.Cell index={1} colSpan={1} className="text-center">
          {answerCards?.userTotalScore} / {answerCards?.totalScore}
        </Table.Summary.Cell>
      </Table.Summary.Row>
    );
  };

  const onExcelDownload = async () => {
    if (res2) {
      try {
        const columns = generateAnswerColumns({
          examTypeName: res?.data.examTypeName,
          examDiv: getDetailCodeLabel(TE0001, res?.data.examDiv ?? ''),
          examTestCode,
        });
        let rows: Object[] = [];
        let cells: string[] = [];
        for (let i = 0; i < res2.data.length; i++) {
          const { examSubjectCode, subjectName } = res2.data[i];
          const {
            data: { data },
          } = await getExamCheckAnswerCard(examTestCode, examSubjectCode);
          if (i === 0) {
            cells = cells.concat(
              Array.from(
                { length: data.answerInfo.length },
                (v, i) => `C${i + 4}`,
              ),
            );
          } else {
            const lastIndex = +cells[cells.length - 1].split('C')[1];
            cells = cells.concat(
              Array.from(
                { length: data.answerInfo.length },
                (v, i) => `C${lastIndex + 3 + i}`,
              ),
            );
          }
          rows = rows.concat(
            generateAnswerRows(subjectName, examSubjectCode, data.answerInfo),
          );
        }
        generateExcel('test', 'test', rows, columns, setAnswerStyle, cells);
      } catch (e) {
        warningNotification(t('cbt.test.message10'));
      }
    }
  };

  useEffect(() => {
    (async () => {
      if (file) {
        const result = await readAnswerExcel(file);
        for (const { examSubjectCode: esc, body } of result) {
          await postExamCheckAnswerCard(examTestCode, esc, body);
        }
        execute(getExamCheckAnswer(examCode, examTestCode));
        execute2(getExamCheckSubjectInfo(examCode));
      }
    })();
  }, [file]);

  return (
    <>
      {fullMode === true
        ? <Modal
          title={t('cbt.test.title5')}
          visible={visible}
          width={700}
          onCancel={onCancel}
          footer={[
            <div className='comm-btn__area justify-content-center'>
              <AntButton className='btn-basic pink-line' onClick={() => upload()}>
                Excel Upload
              </AntButton>
              <AntButton className='btn-basic green' onClick={onExcelDownload}>
                Excel Download
              </AntButton>
              <AntButton key="submit" className='btn-basic pink' onClick={showConfirm}>
                {t('common.button.save')}
              </AntButton>
              <AntButton key="cancel" className='btn-basic gray' onClick={onCancel}>
                {t('common.button.close')}
              </AntButton>
            </div>
          ]}
        >
          {isLoading || isLoading2 || isLoading3 ? <Loader /> : null}
          <ErrorResponse error={error} />
          <ErrorResponse error={error2} />
          <ErrorResponse error={error3} />
          <ErrorResponse error={submitError} />
          
          <div className="comm-table__area rowType mt-0">
            {res?.data ? (
              <Descriptions bordered column={3}>
                <Descriptions.Item label={t('cbt.speakTest.column3')} span={1}>
                  {getDetailCodeLabel(TE0001, res?.data?.examDiv ?? '')}
                </Descriptions.Item>
                <Descriptions.Item label="Type">
                  {res?.data?.examTypeName}
                </Descriptions.Item>
                <Descriptions.Item label={t('cbt.test.column32')}>
                  {res?.data?.testCompleteDateTime ?? ''}
                </Descriptions.Item>
              </Descriptions>
            ) : null}
          </div>
          <div className="comm-bx__space">
            <div className='comm-btn__area right'>
              <Select
                className="w-110p"
                value={examSubjectCode}
                onChange={setExamSubjectCode}
              >
                {res2?.data.map(({ examSubjectCode, subjectName }) => (
                  <Option value={examSubjectCode} key={examSubjectCode}>
                    {subjectName}
                  </Option>
                ))}
              </Select>
            </div>
          </div>
          <div className='comm-table__area mt-0'>
            <Table
              columns={columns}
              dataSource={answerCards?.answerInfo}
              summary={summary}
              pagination={{
                current: 1,
                pageSize: 99999,
              }}          
            />
          </div>
        </Modal>
        : <Modal
          title={t('cbt.test.title5')}
          visible={visible}
          width={470}
          onCancel={onCancel}
          footer={[
            <div className='comm-btn__area justify-content-center'>
              <AntButton key="cancel" className='btn-basic gray' onClick={onCancel}>
                {t('common.button.close')}
              </AntButton>
            </div>
          ]}
        >
          {isLoading || isLoading2 || isLoading3 ? <Loader /> : null}
          <ErrorResponse error={error} />
          <ErrorResponse error={error2} />
          <ErrorResponse error={error3} />
          <div className="comm-table__area rowType mt-0">
            {res?.data ? (
              <Descriptions bordered column={3}>
                <Descriptions.Item label={t('cbt.speakTest.column3')} span={1}>
                  {getDetailCodeLabel(TE0001, res?.data?.examDiv ?? '')}
                </Descriptions.Item>
                <Descriptions.Item label="Type">
                  {res?.data?.examTypeName}
                </Descriptions.Item>
                <Descriptions.Item label={t('cbt.test.column32')}>
                  {res?.data?.testCompleteDateTime ?? ''}
                </Descriptions.Item>
              </Descriptions>
            ) : null}
          </div>
          <div className="comm-bx__space">
            <div className='comm-btn__area right'>
              <Select
                className="w-25"
                value={examSubjectCode}
                onChange={setExamSubjectCode}
              >
                {res2?.data.map(({ examSubjectCode, subjectName }) => (
                  <Option value={examSubjectCode} key={examSubjectCode}>
                    {subjectName}
                  </Option>
                ))}
              </Select>
            </div>
          </div>
          <div className='comm-table__area mt-0'>
            <Table
              columns={summaryColumns}
              dataSource={answerCards?.answerInfo}
              pagination={{
                current: 1,
                pageSize: 99999,
              }}          
            />
          </div>
        </Modal>
      }
      {confirmVisible && (
        <Modal visible={confirmVisible} onOk={onSubmit} onCancel={closeConfirm}>
          <p>{t('cbt.test.text4')}</p>
        </Modal>
      )}
    </>
  );
};

export default AnswerModal;
